import { useEffect, useState } from 'react';
import language from '../../variables/language';

function flattenNode(node) {
   return {
      name: node.name || '',
      description: node.description || '',
      visible: node.visible !== false,
   };
}

// Generic {name, description[, visible]} editor reused by Course + all 5
// hierarchy levels (Module..Topic) - they all share this exact shape.
// Auto-saves on change instead of a staged dirty+Save button, per explicit
// user request: text fields commit on blur, the visible toggle commits
// immediately on click. onSave receives a full {name, description[,
// visible]} snapshot and must return a promise resolving to the freshly-
// saved node.
//
// `levelKey` drives the visible/hidden toggle - every level has it except
// Course itself (Course has no `visible` column at all, per explicit user
// scope: "up to and including Module", Course excluded), so the toggle only
// renders when levelKey is passed and isn't 'course'.
export default function HierarchyNodeForm({ node, onSave, title, levelKey }) {
   const lang = language[language.lang];
   const [formValues, setFormValues] = useState(null);
   const [saving, setSaving] = useState(false);

   useEffect(() => {
      setFormValues(flattenNode(node));
   }, [node]);

   if(!formValues) return '';

   const showVisibilityToggle = levelKey && levelKey !== 'course';

   const persist = (next) => {
      setFormValues(next);
      setSaving(true);
      onSave(next)
         .then((saved) => { if(saved) setFormValues(flattenNode(saved)); })
         .finally(() => setSaving(false));
   };

   const handleFieldChange = (field, value) => {
      setFormValues(prev => ({ ...prev, [field]: value }));
   };

   const handleBlurCommit = () => {
      persist(formValues);
   };

   const handleToggleVisible = () => {
      persist({ ...formValues, visible: !formValues.visible });
   };

   return (
      <div className='course-node-form'>
         <div className='course-node-form-header'>
            <span className='course-node-form-level-label'>{title}</span>
            <div className='course-node-form-header-right'>
               {saving ? <span className='course-node-form-saving-hint'>{lang['Loading ...']}</span> : ''}
               {showVisibilityToggle ? (
                  <span className={'course-node-form-visibility-toggle' + (formValues.visible ? ' visible' : ' hidden')}
                     onClick={handleToggleVisible}>
                     <i className='course-node-form-visibility-dot'></i>
                     {formValues.visible ? lang.visible : lang.hidden}
                  </span>
               ) : ''}
            </div>
         </div>
         <input className='course-node-form-name' value={formValues.name}
            placeholder={lang.name} onChange={e => handleFieldChange('name', e.target.value)}
            onBlur={handleBlurCommit} onKeyDown={e => { if(e.key === 'Enter') e.target.blur(); }} />
         <textarea className='course-node-form-description' rows={5}
            placeholder={lang.description} value={formValues.description}
            onChange={e => handleFieldChange('description', e.target.value)}
            onBlur={handleBlurCommit} />
      </div>
   );
}
